import React, { useState, useEffect, useRef } from "react";
import { Card, PageHeader, Button, Select } from "../../../components/common";
import {
  useAppDispatch,
  useAppSelector,
  searchContentThunk,
  getContentByIdThunk,
  createContentThunk,
  editContentThunk,
  setSelectedContent,
  setExpandedContentId,
} from "../../../redux";
import { showSuccess } from "../../../services";
import { ClassContentRecord, EditClassContentRequest } from "../../../types";
import { getYouTubeEmbedUrl } from "../../../utils";
import plusIcon from "../../../assets/icons/plus.svg";
import minusIcon from "../../../assets/icons/minus.svg";
import chevronIcon from "../../../assets/icons/chevron-down.svg";
import youtubeIcon from "../../../assets/icons/youtube.svg";
import "./TeacherContent.css";

const CLASS_LEVEL_OPTIONS = ["Level 1", "Level 2", "Level 3", "Level 4"];
const CONTENT_WEEK_OPTIONS = Array.from({ length: 40 }, (_, i) => i + 1);
const TYPE_OPTIONS = ["Arts", "Music", "Dance"];

const TeacherContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const records = useAppSelector((state) => state.content.records);
  const selectedContent = useAppSelector(
    (state) => state.content.selectedContent,
  );
  const expandedContentId = useAppSelector(
    (state) => state.content.expandedContentId,
  );

  const [activeTab, setActiveTab] = useState<
    "Create Content" | "Search Content"
  >("Create Content");

  // Form State for Creating Content
  const [classLevel, setClassLevel] = useState<string>("");
  const [contentWeek, setContentWeek] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [youTubeVideos, setYouTubeVideos] = useState<string[]>([""]);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Video player state
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);

  const handleAddLinkField = () => {
    if (youTubeVideos.length < 3) {
      setYouTubeVideos((prev) => [...prev, ""]);
    }
  };

  const handleRemoveLinkField = (index: number) => {
    if (youTubeVideos.length > 1) {
      setYouTubeVideos((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleLinkChange = (index: number, value: string) => {
    setYouTubeVideos((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  // Form State for Editing Content
  const [editingItem, setEditingItem] = useState<ClassContentRecord | null>(
    null,
  );
  const [editClassLevel, setEditClassLevel] = useState<string>("");
  const [editContentWeek, setEditContentWeek] = useState<string>("");
  const [editName, setEditName] = useState<string>("");
  const [editType, setEditType] = useState<string>("");
  const [editDescription, setEditDescription] = useState<string>("");
  const [editYouTubeVideos, setEditYouTubeVideos] = useState<string[]>([""]);

  const handleEditAddLinkField = () => {
    if (editYouTubeVideos.length < 3) {
      setEditYouTubeVideos((prev) => [...prev, ""]);
    }
  };

  const handleEditRemoveLinkField = (index: number) => {
    if (editYouTubeVideos.length > 1) {
      setEditYouTubeVideos((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleEditLinkChange = (index: number, value: string) => {
    setEditYouTubeVideos((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleOpenEditModal = (item: ClassContentRecord) => {
    setEditingItem(item);
    setEditClassLevel(item.Class_Level || "");
    setEditContentWeek(String(item.Content_Week || ""));
    setEditName(item.Name || "");
    setEditType(item.Type || "");
    setEditDescription(item.Description || "");
    const videos = [
      item.YouTube_Video_1,
      item.YouTube_Video_2,
      item.YouTube_Video_3,
    ].filter((v): v is string => Boolean(v && v.trim()));
    setEditYouTubeVideos(videos.length > 0 ? videos : [""]);
  };

  const handleCloseEditModal = () => {
    setEditingItem(null);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !user?.id) return;

    const teacherId = editingItem.Teacher?.id || user.id;

    const payload: EditClassContentRequest = {
      module: "Class_Content",
      recordId: editingItem.id,
      fields: {
        Name: editName,
        Class_Level: editClassLevel,
        Content_Week: Number(editContentWeek),
        Description: editDescription,
        Teacher: teacherId,
        Type: editType,
        YouTube_Video_1: editYouTubeVideos[0] || "",
        YouTube_Video_2: editYouTubeVideos[1] || "",
        YouTube_Video_3: editYouTubeVideos[2] || "",
      },
      trigger: ["workflow"],
    };

    dispatch(editContentThunk(payload)).then((res) => {
      if (editContentThunk.fulfilled.match(res)) {
        showSuccess("Class Content updated successfully!");
        setEditingItem(null);
        executeSearch(undefined, undefined, undefined, true);
        if (expandedContentId === editingItem.id) {
          dispatch(getContentByIdThunk(editingItem.id));
        }
      }
    });
  };

  // Search Filter State
  const [searchLevel, setSearchLevel] = useState<string>("");
  const [searchWeekStart, setSearchWeekStart] = useState<number | string>("");
  const [searchWeekEnd, setSearchWeekEnd] = useState<number | string>("");

  const executeSearch = (
    levelFilter?: string,
    startW?: number | string,
    endW?: number | string,
    force = false,
  ) => {
    const level = levelFilter !== undefined ? levelFilter : searchLevel;
    const start =
      startW !== undefined && startW !== ""
        ? startW
        : searchWeekStart !== ""
          ? searchWeekStart
          : 1;
    const end =
      endW !== undefined && endW !== ""
        ? endW
        : searchWeekEnd !== ""
          ? searchWeekEnd
          : 40;

    let query = `select id, Name, Class_Level, Content_Week, YouTube_Video_1, YouTube_Video_2, YouTube_Video_3, Teacher, Description, Type from Class_Content where Content_Week between ${start} and ${end} order by Content_Week asc limit 200`;

    if (level.trim() !== "") {
      query = `select id, Name, Class_Level, Content_Week, YouTube_Video_1, YouTube_Video_2, YouTube_Video_3, Teacher, Description, Type from Class_Content where Class_Level = '${level}' and Content_Week between ${start} and ${end} order by Content_Week asc limit 200`;
    }

    dispatch(searchContentThunk({ query, force }));
  };

  useEffect(() => {
    executeSearch("", 1, 40, false);
  }, []);

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    const payload = {
      module: "Class_Content",
      fields: {
        Name: name,
        Class_Level: classLevel,
        Content_Week: Number(contentWeek),
        Description: description,
        Teacher: user.id,
        Type: type,
        YouTube_Video_1: youTubeVideos[0] || "",
        YouTube_Video_2: youTubeVideos[1] || "",
        YouTube_Video_3: youTubeVideos[2] || "",
      },
      trigger: ["workflow"],
      files,
    };

    dispatch(createContentThunk(payload)).then((res) => {
      if (createContentThunk.fulfilled.match(res)) {
        showSuccess("Class Content created successfully!");
        setClassLevel("");
        setContentWeek("");
        setName("");
        setType("");
        setDescription("");
        setYouTubeVideos([""]);
        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        executeSearch(undefined, undefined, undefined, true);
      }
    });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    executeSearch(undefined, undefined, undefined, true);
  };

  const handleToggleExpand = (item: ClassContentRecord) => {
    if (expandedContentId === item.id) {
      dispatch(setExpandedContentId(null));
      setActiveVideoIndex(null);
    } else {
      dispatch(setExpandedContentId(item.id));
      setActiveVideoIndex(null);
      dispatch(getContentByIdThunk(item.id));
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Header */}
      <Card
        className="content-page-card content-header-card"
        style={{ padding: "24px 24px 0 24px" }}
      >
        <PageHeader
          title="Content Management"
          subtitle="Create, search, and view class content."
        />
        <div className="content-tabs">
          <button
            type="button"
            className={`tab-btn ${activeTab === "Create Content" ? "active" : ""}`}
            onClick={() => setActiveTab("Create Content")}
          >
            Create Content
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === "Search Content" ? "active" : ""}`}
            onClick={() => setActiveTab("Search Content")}
          >
            Search Content
          </button>
        </div>
      </Card>

      {/* Create Content Form */}
      {activeTab === "Create Content" && (
        <Card style={{ padding: "24px" }}>
          <h3 style={{ margin: "0 0 16px 0" }}>Create Class Content</h3>
          <form onSubmit={handleCreateSubmit} className="content-form-grid">
            <div className="content-field">
              <label>Class Level</label>
              <Select
                options={CLASS_LEVEL_OPTIONS}
                placeholder="Select Class Level"
                value={classLevel}
                onChange={(e) => setClassLevel(e.target.value)}
                required
              />
            </div>

            <div className="content-field">
              <label>Content Week</label>
              <Select
                options={CONTENT_WEEK_OPTIONS}
                placeholder="Select Content Week"
                value={contentWeek}
                onChange={(e) => setContentWeek(e.target.value)}
                required
              />
            </div>

            <div className="content-field">
              <label>Content Name</label>
              <input
                type="text"
                placeholder="Enter content name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="content-field">
              <label>Type</label>
              <Select
                options={TYPE_OPTIONS}
                placeholder="Select Type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                required
              />
            </div>

            <div className="content-field full-width">
              <label>Description</label>
              <textarea
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />
            </div>

            <div className="content-field full-width">
              <label>Files (optional)</label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </div>

            {youTubeVideos.map((videoUrl, index) => {
              const isLast = index === youTubeVideos.length - 1;
              return (
                <div key={index} className="content-field full-width">
                  <label>YouTube Video {index + 1}</label>
                  <div className="link-input-row">
                    <input
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={videoUrl}
                      onChange={(e) => handleLinkChange(index, e.target.value)}
                    />
                    {isLast && youTubeVideos.length < 3 && (
                      <button
                        type="button"
                        className="add-link-btn"
                        onClick={handleAddLinkField}
                        title="Add video link"
                        aria-label="Add video link"
                      >
                        <img src={plusIcon} alt="Add link" />
                      </button>
                    )}
                    {youTubeVideos.length > 1 && (
                      <button
                        type="button"
                        className="remove-link-btn"
                        onClick={() => handleRemoveLinkField(index)}
                        title="Remove video link"
                        aria-label="Remove video link"
                      >
                        <img src={minusIcon} alt="Remove link" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            <div
              className="content-field full-width"
              style={{ marginTop: "8px" }}
            >
              <Button type="submit" variant="primary" size="medium">
                Create Class Content
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Search Content */}
      {activeTab === "Search Content" && (
        <Card style={{ padding: "24px" }}>
          <h3 style={{ margin: "0 0 16px 0" }}>Search Class Content</h3>
          <form onSubmit={handleSearchSubmit} className="content-search-bar">
            <div className="content-field">
              <label>Class Level Filter</label>
              <Select
                options={CLASS_LEVEL_OPTIONS}
                placeholder="e.g. 1 or Level 4"
                value={searchLevel}
                onChange={(e) => setSearchLevel(e.target.value)}
              />
            </div>

            <div className="content-field">
              <label>Start Week</label>
              <Select
                options={CONTENT_WEEK_OPTIONS}
                placeholder="Select Start Week"
                value={searchWeekStart}
                onChange={(e) =>
                  setSearchWeekStart(
                    e.target.value ? Number(e.target.value) : "",
                  )
                }
              />
            </div>

            <div className="content-field">
              <label>End Week</label>
              <Select
                options={CONTENT_WEEK_OPTIONS}
                placeholder="Select End Week"
                value={searchWeekEnd}
                onChange={(e) =>
                  setSearchWeekEnd(e.target.value ? Number(e.target.value) : "")
                }
              />
            </div>

            <div
              className="content-field"
              style={{ justifyContent: "flex-end" }}
            >
              <Button type="submit" variant="secondary" size="medium">
                Search Content
              </Button>
            </div>
          </form>

          {/* Content List with Inline Expandable View */}
          <div className="content-records-list">
            {records.map((item) => {
              const isExpanded = expandedContentId === item.id;

              return (
                <div
                  key={item.id}
                  className={`content-record-item ${isExpanded ? "expanded" : ""}`}
                >
                  <div
                    className="content-record-header"
                    onClick={() => handleToggleExpand(item)}
                  >
                    <div className="content-main-info">
                      <div className="content-title-row">
                        <span className="content-name">{item.Name}</span>
                        <div className="content-badges">
                          <span className="content-badge">
                            Level: {item.Class_Level}
                          </span>
                          <span className="content-badge">
                            Week {item.Content_Week}
                          </span>
                          {item.Type && (
                            <span className="content-badge">{item.Type}</span>
                          )}
                        </div>
                      </div>
                      <span className="content-description">
                        {item.Description}
                      </span>
                    </div>

                    <div
                      className="content-item-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditModal(item);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => handleToggleExpand(item)}
                      >
                        {isExpanded ? "Hide Details" : "Details"}
                      </Button>
                      <img
                        src={chevronIcon}
                        alt="Toggle Details"
                        className={`expand-icon ${isExpanded ? "expanded" : ""}`}
                        onClick={() => handleToggleExpand(item)}
                      />
                    </div>
                  </div>

                  {isExpanded && (() => {
                    const isLoaded = selectedContent && selectedContent.id === item.id;
                    const detail = isLoaded ? selectedContent : item;
                    const videos = [
                      detail.YouTube_Video_1,
                      detail.YouTube_Video_2,
                      detail.YouTube_Video_3,
                    ].filter((v): v is string => Boolean(v && v.trim()));

                    return (
                      <div className="content-expanded-details">
                        {/* Section 1: Content Overview */}
                        <div className="detail-section">
                          <h4 className="detail-section-title">Content Overview</h4>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <span className="detail-label">Name</span>
                              <span className="detail-value">{detail.Name}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Class Level</span>
                              <span className="detail-value">{detail.Class_Level}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Content Week</span>
                              <span className="detail-value">{detail.Content_Week}</span>
                            </div>
                            {item.Type && (
                              <div className="detail-item">
                                <span className="detail-label">Type</span>
                                <span className="detail-value">{item.Type}</span>
                              </div>
                            )}
                            {detail.Description && (
                              <div className="detail-item full-width">
                                <span className="detail-label">Description</span>
                                <span className="detail-value">{detail.Description}</span>
                              </div>
                            )}
                            {isLoaded && selectedContent.Tag && selectedContent.Tag.length > 0 && (
                              <div className="detail-item full-width">
                                <span className="detail-label">Tags</span>
                                <span className="detail-value">{selectedContent.Tag.join(", ")}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Section 2: Video Resources */}
                      <div className="detail-section">
                        <h4 className="detail-section-title">Video Resources</h4>
                        {videos.length === 0 ? (
                          <p className="no-videos-text">
                            No video resources attached to this content.
                          </p>
                        ) : (
                          <div className="youtube-section-container">
                            <div className="youtube-buttons-row">
                              {videos.map((vUrl, vIdx) => {
                                const embed = getYouTubeEmbedUrl(vUrl);
                                if (!embed) return null;
                                const isActive = activeVideoIndex === vIdx;
                                return (
                                  <button
                                    key={vIdx}
                                    type="button"
                                    className={`youtube-video-btn ${isActive ? "active" : ""}`}
                                    onClick={() =>
                                      setActiveVideoIndex(isActive ? null : vIdx)
                                    }
                                  >
                                    <img
                                      src={youtubeIcon}
                                      alt="YouTube"
                                      className="youtube-btn-icon"
                                    />
                                    <span>
                                      {isActive
                                        ? `Close Video ${vIdx + 1}`
                                        : `Watch Video ${vIdx + 1}`}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>

                            {activeVideoIndex !== null && videos[activeVideoIndex] && (
                              <div className="youtube-player-wrapper">
                                <iframe
                                  src={getYouTubeEmbedUrl(videos[activeVideoIndex])!}
                                  title={`YouTube Video ${activeVideoIndex + 1}`}
                                  className="youtube-player-iframe"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                  allowFullScreen
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Edit Content Modal */}
      {editingItem && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div
            className="modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">Edit Class Content</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={handleCloseEditModal}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="modal-form">
              <div className="modal-body content-form-grid">
                <div className="content-field">
                  <label>Class Level</label>
                  <Select
                    options={CLASS_LEVEL_OPTIONS}
                    placeholder="Select Class Level"
                    value={editClassLevel}
                    onChange={(e) => setEditClassLevel(e.target.value)}
                    required
                  />
                </div>

                <div className="content-field">
                  <label>Content Week</label>
                  <Select
                    options={CONTENT_WEEK_OPTIONS}
                    placeholder="Select Content Week"
                    value={editContentWeek}
                    onChange={(e) => setEditContentWeek(e.target.value)}
                    required
                  />
                </div>

                <div className="content-field">
                  <label>Content Name</label>
                  <input
                    type="text"
                    placeholder="Enter content name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                  />
                </div>

                <div className="content-field">
                  <label>Type</label>
                  <Select
                    options={TYPE_OPTIONS}
                    placeholder="Select Type"
                    value={editType}
                    onChange={(e) => setEditType(e.target.value)}
                    required
                  />
                </div>

                <div className="content-field full-width">
                  <label>Description</label>
                  <textarea
                    placeholder="Enter description"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    required
                  />
                </div>

                {editYouTubeVideos.map((videoUrl, index) => {
                  const isLast = index === editYouTubeVideos.length - 1;
                  return (
                    <div key={index} className="content-field full-width">
                      <label>YouTube Video {index + 1}</label>
                      <div className="link-input-row">
                        <input
                          type="url"
                          placeholder="https://www.youtube.com/watch?v=..."
                          value={videoUrl}
                          onChange={(e) =>
                            handleEditLinkChange(index, e.target.value)
                          }
                        />
                        {isLast && editYouTubeVideos.length < 3 && (
                          <button
                            type="button"
                            className="add-link-btn"
                            onClick={handleEditAddLinkField}
                            title="Add video link"
                            aria-label="Add video link"
                          >
                            <img src={plusIcon} alt="Add link" />
                          </button>
                        )}
                        {editYouTubeVideos.length > 1 && (
                          <button
                            type="button"
                            className="remove-link-btn"
                            onClick={() => handleEditRemoveLinkField(index)}
                            title="Remove video link"
                            aria-label="Remove video link"
                          >
                            <img src={minusIcon} alt="Remove link" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="modal-footer">
                <Button
                  type="button"
                  variant="outline"
                  size="medium"
                  onClick={handleCloseEditModal}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" size="medium">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherContent;
